/* Map raw catalogue ItemRow -> display-ready PortalProduct, and the small
   vocab the Add-products filters use. Field names match the VPS contract. */

import { formatIngredients } from '@/utils/ingredients';
import { productPhotoUrl } from '@/portal/api/client';
import type { ItemRow, PortalProduct, SampleAvailability } from '@/portal/api/types';

/** Human-facing sample-request timeline for each availability tier. */
export const SAMPLE_TIMELINE: Record<
  SampleAvailability,
  { label: string; detail: string; tone: 'ready' | 'recent' | 'enquire' }
> = {
  ready: { label: 'Samples in ~2 weeks', detail: 'In stock or currently in production', tone: 'ready' },
  recent: { label: 'Samples in ~3 weeks', detail: 'Made to order — produced recently', tone: 'recent' },
  enquire: { label: 'Call to discuss availability', detail: 'Contact us to plan a sample', tone: 'enquire' },
};

/** Allergen flags offered as "Free from" filters. */
export const ALLERGENS = ['Gluten', 'Milk', 'Egg', 'Nuts', 'Soya', 'Sulphites'] as const;

/** Suitability tags offered as "Suitable for" filters. */
export const SUITABILITY = ['Vegetarian', 'Vegan', 'Gluten Free', 'Nut Free', 'Dairy Free'] as const;

function allergensOf(it: ItemRow): string[] {
  const out: string[] = [];
  if (it.item_is_gluten) out.push('Gluten');
  if (it.item_is_egg) out.push('Egg');
  if (it.item_is_dairy) out.push('Milk');
  if (it.item_is_nuts) out.push('Nuts');
  if (it.item_is_soya) out.push('Soya');
  if (it.item_is_sulphites) out.push('Sulphites');
  if (it.item_is_fish) out.push('Fish');
  if (it.item_is_celery) out.push('Celery');
  if (it.item_is_mustard) out.push('Mustard');
  if (it.item_is_sesame) out.push('Sesame');
  if (it.item_is_lupin) out.push('Lupin');
  if (it.item_is_mollusc) out.push('Molluscs');
  return out;
}

function suitabilityOf(it: ItemRow): string[] {
  const out: string[] = [];
  if (it.item_is_vegetarian) out.push('Vegetarian');
  if (it.item_is_vegan) out.push('Vegan', 'Dairy Free');
  if (it.item_is_halal) out.push('Halal');
  if (!it.item_is_gluten) out.push('Gluten Free');
  if (!it.item_is_nuts) out.push('Nut Free');
  if (!it.item_is_dairy && !it.item_is_vegan) out.push('Dairy Free');
  return [...new Set(out)];
}

function statusOf(it: ItemRow): PortalProduct['status'] {
  return it.itemislive ? 'live' : 'draft';
}

export function toProduct(it: ItemRow): PortalProduct {
  const code = (it.itemcode || `ITEM${it.itemid}`).trim();
  const name = it.item_marketingproducttitle || it.itemdescription || code;
  const photo = productPhotoUrl(code);
  // Description: prefer the marketing copy; fall back to the plain item
  // description. Suppress it when it merely repeats the name (common when there
  // is no marketing copy and both fall back to itemdescription) to avoid the
  // title showing twice on cards/slides/PDF.
  const rawDesc = it.item_marketingdescription || it.itemdescription || undefined;
  const desc =
    rawDesc && rawDesc.trim().toLowerCase() !== name.trim().toLowerCase() ? rawDesc : undefined;
  // Weight shown is per-UNIT = case (final) weight ÷ case size, rounded (no decimals).
  // e.g. 744g / 24 = 31g. Falls back to the raw value when case size is unknown.
  const unitWeight =
    it.final_weight != null && it.pack_size
      ? Math.round(Number(it.final_weight) / Number(it.pack_size))
      : it.final_weight != null
        ? Math.round(Number(it.final_weight))
        : null;
  return {
    id: code,
    code,
    name,
    // Ranges in the portal are grouped by Category6 (ItemTbl.Category6ID_FK →
    // Category6_Tbl), the FP sub-category — not the website category.
    rangeKey: it.category6id_fk != null ? String(it.category6id_fk) : 'other',
    rangeLabel: it.category6_description || 'Other',
    img: photo,
    imgHi: photo,
    desc,
    allergens: allergensOf(it),
    suitability: suitabilityOf(it),
    price: it.item_webprice != null ? `\u00a3${Number(it.item_webprice).toFixed(2)}` : undefined,
    weight: unitWeight != null ? `${unitWeight} g` : undefined,
    packSize: it.pack_size != null ? String(it.pack_size) : undefined,
    ingredients: it.item_ingredientlist ? formatIngredients(it.item_ingredientlist) : undefined,
    status: statusOf(it),
    availability: it.sample_availability ?? undefined,
  };
}

export interface RangeGroup {
  key: string;
  label: string;
}

/** Reorder products grouped by category (Category6 range). Category order =
    first appearance; order within a category is preserved. Mirrors the
    portfolio editor's grouped layout so slides/PDF match the on-screen order. */
export function orderByCategory(products: PortalProduct[]): PortalProduct[] {
  const order: string[] = [];
  const map = new Map<string, PortalProduct[]>();
  products.forEach((p) => {
    if (!map.has(p.rangeKey)) {
      map.set(p.rangeKey, []);
      order.push(p.rangeKey);
    }
    map.get(p.rangeKey)!.push(p);
  });
  return order.flatMap((k) => map.get(k)!);
}

/** Distinct ranges present in a product set, sorted by label. */
export function rangesOf(products: PortalProduct[]): RangeGroup[] {
  const map = new Map<string, string>();
  products.forEach((p) => {
    if (!map.has(p.rangeKey)) map.set(p.rangeKey, p.rangeLabel);
  });
  return [...map.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
